package fr.frux.feature.map

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.SizeTransform
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.snap
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.animation.slideOutVertically
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.calculateEndPadding
import androidx.compose.foundation.layout.calculateStartPadding
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBars
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.SnackbarDuration
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.SnackbarResult
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import fr.frux.core.ads.MapBannerAd
import fr.frux.core.ads.rememberMapBannerState
import fr.frux.core.ads.rewardedResultMessage
import fr.frux.core.designsystem.components.ContributionGuideSheet
import fr.frux.core.designsystem.components.FruxSnackbar
import fr.frux.core.designsystem.components.SnackbarMessageEffect
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.layout.FruxLayoutMode
import fr.frux.core.designsystem.layout.LocalFruxLayoutMode
import fr.frux.core.designsystem.layout.LocalRootChromeInsets
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.maps.Location
import fr.frux.core.maps.LocationPermissionStatus
import fr.frux.core.maps.rememberLocationPermissionController
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_confirmation_view
import fr.frux.feature.map.resources.map_login_prompt_action
import fr.frux.feature.map.resources.map_login_prompt_message
import fr.frux.feature.map.site.SiteNoticeEffects
import fr.frux.feature.map.site.SiteSummaryCard
import fr.frux.shared.domain.model.MapFilter
import fr.frux.shared.domain.model.inSeasonHighlights
import org.jetbrains.compose.resources.stringResource

/**
 * Chrome de la carte, jamais rogné par le Scaffold racine (ADR 0009 : la carte doit rester pleine
 * hauteur) : le dégagement du chrome racine est donc consommé explicitement ici, pas par un
 * padding de layout, via [LocalRootChromeInsets] (#85, quatre côtés depuis le lot 2 grand écran),
 * plus une marge visuelle propre à cet écran (recherche, fiche, actions flottantes).
 */
@Composable
private fun bottomNavClearance(): Dp = LocalRootChromeInsets.current.calculateBottomPadding() + FruxSpacing.md

/** Marge laterale d'un cadrage : de quoi ne pas coller les marqueurs au bord de l'ecran. */
private val FRAME_SIDE_PADDING = 48.dp

/**
 * Explorer (Map) screen — le **chrome** de la carte, sur fond transparent.
 *
 * La vue carte elle-meme n'est pas ici : c'est la surface persistante composee par App sous le
 * NavDisplay ([MapSurface], #124), jamais demontee. Cet ecran ne dessine que les surcouches —
 * recherche, filtres, bandeau de localisation, fiche d'arbre, actions flottantes — et peut etre
 * detruit/recree librement par la navigation : tout l'etat durable vit dans [MapHostState].
 *
 * Features:
 * - Floating pill search bar
 * - Filtres reels par saison, partie recoltee et forme de plante
 * - Non-modal bottom card for the selected tree, anchored above the bottom nav
 *
 * L'ajout d'arbre n'a pas de FAB ici : c'est la pastille « + » de la barre du bas (#199) ou du
 * rail grand écran, commune à tous les onglets. En grand écran (`LocalFruxLayoutMode`), la
 * recherche se borne à 480 dp en haut à gauche et la fiche flottante à 640 dp centrés (spec
 * 2026-09-10 §5).
 */
@Composable
fun MapScreen(
    host: MapHostState,
    onTreeClick: (Long) -> Unit = {},
    /** Ouvre la fiche d'un lieu : résumé de la carte ou « Voir » de l'avis de création. */
    onSiteClick: (Long) -> Unit = {},
    onSeeAllClick: (SeeAllContext) -> Unit = {},
    onStartHarvest: (Long) -> Unit = {},
    onLogin: () -> Unit = {},
    onDirections: (latitude: Double, longitude: Double, label: String) -> Unit = { _, _, _ -> },
    /**
     * L'accueil vient de trancher la position sans l'accorder (#502) : voir
     * [MapViewModel.onLocationSettledByOnboarding].
     */
    locationSettledByOnboarding: Boolean = false,
    /**
     * « Recenser un arbre que je connais » de la carte de zone vide (spec onboarding de
     * contribution §2). [guideRead] vrai quand la personne vient de lire « Que puis-je recenser ? ».
     */
    onAddTree: (guideRead: Boolean) -> Unit = {},
    /** « Recenser un autre arbre » de la feuille du premier arbre (spec §4). */
    onAddAnotherTree: () -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val viewModel = host.viewModel
    val state by viewModel.state.collectAsState()
    val siteState by host.sites.state.collectAsState()
    val snackbarHostState = remember { SnackbarHostState() }
    val markerGroupTrees =
        remember(state.markerGroupIds, state.visibleTrees) {
            val ids = state.markerGroupIds.toSet()
            state.visibleTrees.filter { tree -> tree.id in ids }
        }

    val camera = host.camera
    val permission = rememberLocationPermissionController()
    val bottomClearance = bottomNavClearance()
    val focusManager = LocalFocusManager.current
    // Etat purement local (#133) : la sheet redemarre fermee a chaque retour sur l'onglet carte
    // (l'ecran, contrairement a la surface persistante, est recree — ADR 0009), ce qui est le
    // comportement voulu pour une aide contextuelle.
    var showLegend by remember { mutableStateOf(false) }
    // Meme raison que [showLegend] : la feuille de filtres (#262) est une surface transitoire, pas
    // un etat durable — seuls les filtres CHOISIS vivent dans le ViewModel.
    var showFilters by remember { mutableStateOf(false) }
    // Aide « Que puis-je recenser ? » (spec §3), feuille transitoire comme la légende.
    var showContributionGuide by remember { mutableStateOf(false) }
    // Idem depuis #263 : la liste « autour de toi » n'est plus le fond permanent de l'ecran mais
    // une feuille modale ouverte a la demande, au meme titre que la legende. Elle redemarre donc
    // fermee a chaque retour sur l'onglet carte, ce qui est le comportement voulu.
    var showNearbyList by remember { mutableStateOf(false) }

    // Bannière au repos (#529) : demandée seulement quand rien n'occupe le bas de la carte, puis
    // soumise à l'arbitrage du contrôleur (une seule annonce visible, éligibilité).
    val banner =
        rememberMapBannerState(
            requested =
                mapBannerRequested(
                    selectedTreeOpen = state.selectedTree != null,
                    selectedSiteOpen = siteState.selectedSite != null,
                    markerGroupOpen = markerGroupTrees.size > 1,
                    panelOpen =
                        showLegend || showFilters || showNearbyList || showContributionGuide ||
                            state.firstContribution != null,
                    searchActive = state.searchQuery.isNotEmpty(),
                ),
        )

    // Attribution du fond de carte au-dessus de la feuille du bas (ADR 0010) : hauteur mesuree du
    // slot, animee comme la feuille elle-meme, ecrite sur la camera hors `contentPadding` pour ne
    // jamais rejouer le cadrage. Sans mesure, le degagement de la navigation (aucun saut au retour
    // d'onglet).
    var bottomSlotHeight by remember { mutableStateOf<Dp?>(null) }
    val attributionLift by animateDpAsState(
        targetValue = bottomSlotHeight ?: bottomClearance,
        animationSpec = tween(BOTTOM_SLOT_ANIM_MS),
        label = "MapAttributionLift",
    )
    // Lue en composition (et non dans le SideEffect) : sinon l'animation ne relance aucune
    // recomposition et la camera garde la premiere valeur.
    val liftNow = attributionLift
    SideEffect { camera.bottomOverlayInset = liftNow }
    DisposableEffect(camera) { onDispose { camera.bottomOverlayInset = 0.dp } }

    // Hauteur de la seule rangee SearchPill+FilterButton (#163) : mesure locale a cette rangee,
    // stable quelle que soit la pile de bandeaux/dropdown en dessous. `topControlsHeight` mesurait
    // jusqu'ici toute la colonne du haut (bandeau de localisation compris), donc bougeait avec elle
    // — le cadrage camera rejouait a chaque apparition/disparition d'un bandeau.
    var searchRowHeight by remember { mutableStateOf(0.dp) }
    val density = LocalDensity.current
    val statusBarInset = WindowInsets.statusBars.asPaddingValues().calculateTopPadding()
    val rootInsets = LocalRootChromeInsets.current
    val layoutDirection = LocalLayoutDirection.current

    // Les bords de la carte sont occupes par les surcouches : le cadrage doit viser la zone
    // reellement visible, pas la vue entiere. `statusBarInset + sm` reproduit l'espace au-dessus
    // de la rangee de recherche (inset systeme, puis le Spacer place en tete de [topBar]). Les
    // cotes ajoutent le degagement du chrome racine (colonne grand ecran, spec §5).
    val framePadding =
        PaddingValues(
            start = FRAME_SIDE_PADDING + rootInsets.calculateStartPadding(layoutDirection),
            end = FRAME_SIDE_PADDING + rootInsets.calculateEndPadding(layoutDirection),
            top = statusBarInset + FruxSpacing.sm + searchRowHeight + FruxSpacing.md,
            bottom = bottomClearance,
        )
    // Jamais avant la mesure : la couche de rendu rejoue le cadrage en attente a chaque
    // changement de marge, et cet ecran est recree a chaque retour d'onglet (la camera, elle,
    // est permanente — ADR 0009). Ecrire le padding transitoire a 0 puis la valeur mesuree
    // rejouait le cadrage deux fois — la camera bougeait a chaque retour sur la carte. La
    // re-mesure d'un chrome identique reecrit la meme valeur, sans effet.
    if (searchRowHeight > 0.dp) {
        SideEffect { camera.contentPadding = framePadding }
    }

    // Premier contact : la boite de dialogue systeme suffit, pas d'ecran d'amorce maison.
    // Sur le web (promptOnLaunch faux), la demande attend le clic sur « ma position » : Chrome
    // penalise les prompts hors geste utilisateur. Apres un refus ou un « Plus tard » dans
    // l'accueil (#502), la demande attend aussi ce clic : le ViewModel est prevenu AVANT la
    // lecture du statut ci-dessous, pour que le refus deja connu ne pose pas de bandeau.
    LaunchedEffect(Unit) {
        if (locationSettledByOnboarding) viewModel.onLocationSettledByOnboarding()
        val suppressed = viewModel.state.value.locationPromptSuppressed
        if (permission.promptOnLaunch && permission.status == LocationPermissionStatus.NOT_REQUESTED && !suppressed) {
            permission.request()
        }
    }

    LaunchedEffect(permission.status) {
        viewModel.onPermissionResult(permission.status)
    }

    // Retour sur l'ecran carte : le ViewModel est permanent (#124), ce signal de composition est
    // le seul « resume » restant — il recharge le lot d'arbres s'il est perime.
    LaunchedEffect(viewModel) {
        viewModel.onMapBecameVisible()
    }

    // Le libellé de l'action est résolu ici, en composition : stringResource est asynchrone sur js.
    val viewLabel = stringResource(Res.string.map_confirmation_view)
    SnackbarMessageEffect(
        trigger = state.confirmationMessage,
        message = state.confirmationMessage,
        snackbarHostState = snackbarHostState,
        actionLabel = viewLabel.takeIf { state.confirmationTreeId != null },
        duration = SnackbarDuration.Long,
    ) { result ->
        val treeId = state.confirmationTreeId
        if (result == SnackbarResult.ActionPerformed && treeId != null) onTreeClick(treeId)
        viewModel.consumeConfirmation()
    }

    SiteNoticeEffects(host.sites, snackbarHostState, onSiteClick)

    SnackbarMessageEffect(
        trigger = state.favoriteError,
        message = state.favoriteError?.userMessage(),
        snackbarHostState = snackbarHostState,
    ) { viewModel.consumeFavoriteError() }

    SnackbarMessageEffect(
        trigger = state.showLoginPrompt,
        message = if (state.showLoginPrompt) stringResource(Res.string.map_login_prompt_message) else null,
        snackbarHostState = snackbarHostState,
        actionLabel = stringResource(Res.string.map_login_prompt_action),
    ) { result ->
        viewModel.consumeLoginPrompt()
        if (result == SnackbarResult.ActionPerformed) onLogin()
    }

    // Vidéo récompensée (#531) : résultat porté par l'état du VM permanent de la carte.
    SnackbarMessageEffect(
        trigger = state.rewardedResult,
        message = rewardedResultMessage(state.rewardedResult),
        snackbarHostState = snackbarHostState,
    ) { viewModel.consumeRewardedResult() }

    // Ferme le clavier au premier geste sur la carte (tap ou pan, #126/#163). Le signal vient de
    // [MapHostState.mapGestureTick] : la surface carte n'est pas un descendant de cet ecran
    // (ADR 0009), elle ne peut pas appeler `LocalFocusManager` elle-meme.
    LaunchedEffect(host, host.mapGestureTick) {
        if (host.mapGestureTick > 0) focusManager.clearFocus()
    }

    // Grand écran (spec §5) : recherche flottante bornée en haut à gauche, fiche flottante bornée
    // et centrée. Le ViewModel, l'état et les callbacks ne changent pas.
    val expanded = LocalFruxLayoutMode.current == FruxLayoutMode.Expanded

    // Fond transparent : la carte est deja peinte par la surface persistante, sous cet ecran.
    Box(modifier = modifier.fillMaxSize()) {
        // Composes AVANT le scaffold : la fiche d'arbre, les actions et les ecrans d'etat
        // (chargement, erreur) doivent le recouvrir quand ils montent. Places apres, l'erreur
        // flottait par-dessus le bouton « Lancer une recolte » et l'ecran d'erreur.
        when {
            state.isLoading && state.nearbyTrees.isEmpty() -> {
                LoadingOverlay(modifier = Modifier.align(Alignment.Center))
            }

            state.error != null && state.nearbyTrees.isEmpty() -> {
                ErrorOverlay(
                    message = state.error!!.userMessage(),
                    onRetry = viewModel::retry,
                    modifier = Modifier.fillMaxSize(),
                )
            }
        }

        MapOverlayScaffold(
            bottomClearance = bottomClearance,
            topMaxWidth = if (expanded) MapChromeDefaults.ExpandedTopWidth else Dp.Unspecified,
            bottomSlotMaxWidth = if (expanded) MapChromeDefaults.ExpandedBottomSlotWidth else Dp.Unspecified,
            onBottomSlotHeightChange = { bottomSlotHeight = it },
            topBar = {
                Spacer(modifier = Modifier.height(FruxSpacing.sm))

                // Recherche et filtres sur une seule ligne (#262) : la rangee de chips qui occupait
                // la largeur juste en dessous est remplacee par ce bouton unique, pour degager la
                // carte sous la barre. Le badge est le seul reste visible d'un filtre actif.
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
                    modifier =
                        Modifier
                            .fillMaxWidth(0.95f)
                            .align(Alignment.CenterHorizontally)
                            // Mesure locale a cette seule rangee (#163) : c'est elle, et rien
                            // d'autre, qui pilote `camera.contentPadding` plus haut.
                            .onSizeChanged { size ->
                                searchRowHeight = with(density) { size.height.toDp() }
                            },
                ) {
                    SearchPill(
                        query = state.searchQuery,
                        onQueryChange = viewModel::onSearchQueryChange,
                        modifier = Modifier.weight(1f),
                    )

                    if (state.showFilters) {
                        FilterButton(
                            hasActiveFilter = state.filter.isActive || state.selectedSpeciesRollupId != null,
                            onClick = { showFilters = true },
                        )
                    }
                }

                // Spec #394 §4, revue en carte a sections unique : une requete qui nomme une
                // espece garde sa section en tete (voir [placesFirst]).
                val placesFirst = placesFirst(state.speciesSuggestions)
                val hasResults =
                    state.speciesSuggestions.isNotEmpty() ||
                        state.searchResults.isNotEmpty() ||
                        state.placeResults.isNotEmpty()

                if (state.searchQuery.length >= 2 && hasResults) {
                    Spacer(modifier = Modifier.height(FruxSpacing.xs))
                    SearchResultsList(
                        species = state.speciesSuggestions,
                        trees = state.searchResults,
                        places = state.placeResults,
                        placesFirst = placesFirst,
                        // Choisir un resultat termine la saisie (#413) : sans cela le clavier
                        // restait ouvert sur la moitie basse de la carte, juste la ou les arbres
                        // du lieu choisi viennent d'etre recharges. Le tap sur la liste ne passe
                        // pas par la carte, il ne declenche donc aucun `mapGestureTick`.
                        onSpeciesClick = { species ->
                            focusManager.clearFocus()
                            viewModel.onSpeciesSelected(species)
                        },
                        onTreeClick = { tree ->
                            focusManager.clearFocus()
                            viewModel.selectTree(tree.id, SelectionOrigin.LIST)
                            viewModel.onSearchQueryChange("")
                        },
                        onPlaceClick = { place ->
                            focusManager.clearFocus()
                            viewModel.selectPlace(place)
                        },
                        modifier =
                            Modifier
                                .fillMaxWidth(0.95f)
                                .align(Alignment.CenterHorizontally),
                    )
                }

                state.locationNotice?.let { notice ->
                    Spacer(modifier = Modifier.height(FruxSpacing.sm))
                    LocationNoticeBanner(
                        notice = notice,
                        shouldExplain = permission.shouldExplain,
                        canOpenAppSettings = permission.canOpenAppSettings,
                        onAction = {
                            when (notice) {
                                LocationNotice.PERMISSION_DENIED -> {
                                    viewModel.onLocateRequestedPendingPermission()
                                    permission.request()
                                }

                                // Redemander n'afficherait plus rien : les reglages systeme sont la
                                // seule issue, et le bandeau doit y emmener plutot que de s'y referer.
                                LocationNotice.PERMISSION_PERMANENTLY_DENIED -> {
                                    permission.openAppSettings()
                                }

                                LocationNotice.UNAVAILABLE -> {
                                    Unit
                                }
                            }
                        },
                        onDismiss = viewModel::dismissLocationNotice,
                        modifier =
                            Modifier
                                .fillMaxWidth(0.95f)
                                .align(Alignment.CenterHorizontally),
                    )
                }
            },
            topNotices = {
                Spacer(modifier = Modifier.height(FruxSpacing.sm))

                // Un seul bandeau a la fois (#163), choisi par priorite : zone vide filtree >
                // zone vide sans filtre (#478) > pastille de zone peuplee > lot tronque > cache
                // perime. Le chevauchement de jusqu'a 3 bandeaux qui pouvait pousser la pastille
                // tres bas sur un ecran etroit devient impossible par construction.
                val notice =
                    selectMapNotice(
                        filteredNoResults = state.filteredNoResults,
                        truncatedTotal = state.truncatedTotal,
                        shownCount = state.nearbyTrees.size,
                        showingStaleCache = state.showingStaleCache,
                        emptyArea = state.emptyArea,
                        populatedHint = state.populatedHint,
                    )
                AnimatedContent(
                    targetState = notice,
                    transitionSpec = {
                        (slideInVertically { height -> -height } + fadeIn()) togetherWith
                            (slideOutVertically { height -> -height } + fadeOut())
                    },
                    modifier = Modifier.align(Alignment.CenterHorizontally),
                    label = "MapNoticeSlot",
                ) { current ->
                    when (current) {
                        is MapNotice.FilteredEmpty -> {
                            FilteredEmptyBanner(
                                emptiness = current.emptiness,
                                speciesSelected = state.selectedSpeciesRollupId != null,
                                inSeasonOnly = state.filter.query == MapFilter(inSeason = true),
                                onShowNearest = viewModel::showNearestMatches,
                                modifier = Modifier.padding(bottom = FruxSpacing.sm),
                            )
                        }

                        is MapNotice.EmptyArea -> {
                            if (state.showsEmptyAreaCard) {
                                EmptyAreaCard(
                                    nearestDistanceMeters = state.nearestDistanceMeters,
                                    seasonalAround = state.seasonalAround,
                                    onShowNearest = viewModel::showNearestMatches,
                                    onSeasonSpeciesClick = { species ->
                                        focusManager.clearFocus()
                                        viewModel.onSpeciesSelected(species)
                                    },
                                    onAddTree = { onAddTree(false) },
                                    onWhatCanIAdd = { showContributionGuide = true },
                                    onDismiss = viewModel::dismissEmptyAreaCard,
                                    modifier =
                                        Modifier
                                            .fillMaxWidth(0.95f)
                                            .padding(bottom = FruxSpacing.sm),
                                )
                            }
                        }

                        MapNotice.PopulatedHint -> {
                            // Son texte long se replie sur deux lignes en portrait : sans gabarit, la
                            // pastille prenait toute la largeur, hors de l'alignement de la barre.
                            PopulatedAreaHintBanner(
                                onDismiss = viewModel::dismissPopulatedHint,
                                modifier =
                                    Modifier
                                        .fillMaxWidth(0.95f)
                                        .padding(bottom = FruxSpacing.sm),
                            )
                        }

                        is MapNotice.TruncatedBatch -> {
                            TruncatedBatchBanner(
                                shown = current.shown,
                                total = current.total,
                                modifier = Modifier.padding(bottom = FruxSpacing.sm),
                            )
                        }

                        MapNotice.StaleCache -> {
                            StaleCacheBanner(modifier = Modifier.padding(bottom = FruxSpacing.sm))
                        }

                        null -> {
                            Unit
                        }
                    }
                }

                // Action, pas une info : toujours en plus du bandeau ci-dessus, jamais a sa place.
                val searchAreaVisible = state.canSearchArea || state.isSearchingArea
                AnimatedVisibility(
                    visible = searchAreaVisible,
                    enter = slideInVertically { height -> -height } + fadeIn(),
                    exit = slideOutVertically { height -> -height } + fadeOut(),
                    modifier = Modifier.align(Alignment.CenterHorizontally),
                ) {
                    SearchAreaPill(
                        isSearching = state.isSearchingArea,
                        onClick = viewModel::searchThisArea,
                    )
                }

                // Invitation a confirmer un arbre jamais vu (#396) : elle cede la place a tout le
                // reste, cf. [showsNeverSeenInvite]. Elle ouvre la fiche, seul endroit ou le geste
                // porte de l'information (ADR 0025).
                val invite = state.neverSeenInvite

                // L'invitation est effacee de l'etat des le tap ou la fermeture : garder la
                // derniere valeur connue laisse la pastille se peindre pendant sa sortie, au lieu
                // de disparaitre d'un coup (meme raison que les instantanes des feuilles, #205).
                var shownInvite by remember { mutableStateOf<NeverSeenInvite?>(null) }
                LaunchedEffect(invite) { if (invite != null) shownInvite = invite }

                AnimatedVisibility(
                    visible = showsNeverSeenInvite(invite, notice, searchAreaVisible),
                    enter = slideInVertically { height -> -height } + fadeIn(),
                    exit = slideOutVertically { height -> -height } + fadeOut(),
                    modifier = Modifier.align(Alignment.CenterHorizontally),
                ) {
                    val shown = shownInvite
                    if (shown != null) {
                        NeverSeenInvitePill(
                            speciesName = shown.speciesName,
                            onOpen = {
                                viewModel.onNeverSeenInviteAccepted()
                                onTreeClick(shown.treeId)
                            },
                            onDismiss = viewModel::onNeverSeenInviteDismissed,
                        )
                    }
                }
            },
            bottomStart = {
                // Legende seule : l'attribution des donnees d'arbres vit dans le panneau du bouton
                // (i), cf. MapHost. Meme marge de bord que MapListButton a droite : sans elle, le
                // bouton touchait le bord gauche de l'ecran.
                MapLegendButton(
                    onClick = { showLegend = true },
                    modifier = Modifier.padding(start = FruxSpacing.md),
                )
            },
            bottomEnd = {
                // Action carte : seul « me localiser » reste ici, l'ajout d'arbre a migré vers la
                // pastille centrale de la bottom nav (#199) — deux « + » auraient été ambigus.
                // Elle s'efface vers la droite quand la carte d'arbre monte (#84) ou que le sheet
                // « autour de toi » est déployé (#196), pour ne pas passer sous leur contenu.
                AnimatedVisibility(
                    visible = state.selectedTree == null && markerGroupTrees.size <= 1,
                    enter = slideInHorizontally { width -> width } + fadeIn(),
                    exit = slideOutHorizontally { width -> width } + fadeOut(),
                ) {
                    // Colonne d'actions carte : liste puis « me localiser ». La legende vit en
                    // bas a gauche, un controle discret, pas une action.
                    Column(horizontalAlignment = Alignment.End) {
                        // Seul acces a la liste depuis #263 : la feuille « autour de toi » ne
                        // borde plus le bas de l'ecran en permanence, il lui faut donc une
                        // commande explicite.
                        MapListButton(
                            onClick = { showNearbyList = true },
                            modifier = Modifier.padding(end = FruxSpacing.md),
                        )

                        Spacer(modifier = Modifier.height(FruxSpacing.sm))

                        // Point de rentree apres un refus de localisation (#117) : sans lui, un
                        // utilisateur qui a refuse une fois restait definitivement sur la
                        // position de repli.
                        LocateMeButton(
                            isLocating = state.isLocating,
                            onClick = { onLocateRequested(viewModel, permission) },
                            modifier = Modifier.padding(end = FruxSpacing.md),
                        )
                    }
                }
            },
            bottomSlot = {
                // Slot bas unique, priorité descendante (#205) : un groupe de marqueurs
                // sélectionné prend le pas sur la fiche d'un arbre, qui prend elle-même le pas
                // sur le sheet « a proximite » — les trois montreraient sinon le meme
                // emplacement en double.
                //
                // AnimatedContent plutot qu'un `when` nu (#207) : keye sur le TYPE de contenu ET
                // son identite (id d'arbre / ids du groupe), pour qu'une bascule entre deux
                // selections differentes rejoue la transition, pas seulement une bascule entre
                // deux slots differents. Tween par defaut (FastOutSlowInEasing) : PAS de
                // CubicBezierEasing custom, crash connu sur js a epsilon de 1.
                val selectedTree = state.selectedTree
                val selectedSite = siteState.selectedSite
                val slot =
                    mapBottomSlot(
                        markerGroupOpen = markerGroupTrees.size > 1,
                        selectedTreeOpen = selectedTree != null,
                        selectedSiteOpen = selectedSite != null,
                        bannerShowing = banner.showing,
                    )
                val bottomSlotKey =
                    when (slot) {
                        MapBottomSlot.GROUP -> "group:${markerGroupTrees.map { it.id }.sorted()}"

                        MapBottomSlot.TREE -> "tree:${selectedTree?.id}"

                        MapBottomSlot.SITE -> "site:${selectedSite?.id}"

                        // Meme feuille que la fiche d'arbre : elle la remplace, jamais les deux.
                        MapBottomSlot.BANNER -> "banner"

                        // Depuis #263 la liste « autour de toi » n'est plus le fond de ce slot :
                        // au repos il est VIDE et la carte est plein cadre. Elle vit desormais
                        // dans une feuille modale ouverte a la demande, hors de ce slot.
                        MapBottomSlot.NONE -> "none"
                    }
                AnimatedContent(
                    targetState = bottomSlotKey,
                    transitionSpec = {
                        // Taille du conteneur en snap, sans clip : le SizeTransform par défaut
                        // anime la taille depuis 0 (slot « none » vide) avec un contenu épinglé
                        // TopStart — la fiche se révélait en diagonale depuis un coin au lieu de
                        // glisser du bas. En snappant, seuls les slide/fade jouent : entrée et
                        // sortie par le bas, point.
                        (
                            slideInVertically(animationSpec = tween(BOTTOM_SLOT_ANIM_MS)) { it } +
                                fadeIn(animationSpec = tween(BOTTOM_SLOT_ANIM_MS))
                        ) togetherWith
                            (
                                slideOutVertically(animationSpec = tween(BOTTOM_SLOT_ANIM_MS)) { it } +
                                    fadeOut(animationSpec = tween(BOTTOM_SLOT_ANIM_MS))
                            ) using
                            SizeTransform(clip = false, sizeAnimationSpec = { _, _ -> snap() })
                    },
                    contentAlignment = Alignment.BottomCenter,
                    label = "MapBottomSheetSlot",
                ) { slotKey ->
                    // Instantanés par slot : pendant l'animation de SORTIE, ce lambda recompose
                    // avec l'état vivant déjà vidé (groupe dismissé → « 0 arbres », sélection
                    // effacée → le slot sortant retomberait sur le sheet nearby). Le contenu
                    // sortant doit rejouer ses données capturées, pas l'état courant.
                    val groupSnapshot = remember(slotKey) { markerGroupTrees }
                    val selectedSnapshot = remember(slotKey) { selectedTree }
                    val siteSnapshot = remember(slotKey) { selectedSite }
                    when {
                        slotKey.startsWith("group:") -> {
                            MarkerGroupSheet(
                                trees = if (markerGroupTrees.size > 1) markerGroupTrees else groupSnapshot,
                                speciesHarvests = state.speciesHarvests,
                                currentMonth = state.currentMonth,
                                now = state.now,
                                reference = state.distanceReference,
                                onTreeClick = { tree ->
                                    viewModel.dismissMarkerGroup()
                                    viewModel.selectTree(tree.id, SelectionOrigin.MAP)
                                },
                                onDismiss = viewModel::dismissMarkerGroup,
                            )
                        }

                        slotKey.startsWith("tree:") && (selectedTree ?: selectedSnapshot) != null -> {
                            val tree = selectedTree ?: selectedSnapshot!!
                            SelectedTreeCard(
                                tree = tree,
                                status =
                                    tree.resolvedStatus(
                                        state.speciesHarvests[tree.species.id].orEmpty(),
                                        state.currentMonth,
                                        state.now,
                                    ),
                                seasonLabel =
                                    mapSeasonLabel(
                                        state.speciesHarvests[tree.species.id].orEmpty(),
                                        state.currentMonth,
                                    ),
                                reference = state.distanceReference,
                                // state.favoriteIds.contains(tree.id), pas
                                // isSelectedTreeFavorite : pendant l'animation de sortie, tree
                                // vient du snapshot mais l'appartenance reste juste.
                                isFavorite = state.favoriteIds.contains(tree.id),
                                onFavoriteClick = viewModel::onFavoriteClick,
                                onCardClick = { onTreeClick(tree.id) },
                                onStartHarvest = { onStartHarvest(tree.id) },
                                onDirections = {
                                    onDirections(tree.latitude, tree.longitude, tree.species.commonNameFr)
                                },
                                onClose = viewModel::clearSelection,
                            )
                        }

                        slotKey.startsWith("site:") && (selectedSite ?: siteSnapshot) != null -> {
                            val site = selectedSite ?: siteSnapshot!!
                            SiteSummaryCard(
                                site = site,
                                // Position connue seulement : pas de repli sur le centre interroge,
                                // une distance inventee serait pire qu'aucune.
                                distanceMeters =
                                    state.userLocation?.let {
                                        Location(
                                            site.latitude,
                                            site.longitude,
                                        ).distanceMetersTo(it)
                                    },
                                nowMillis = state.now.toEpochMilliseconds(),
                                onOpen = { onSiteClick(site.id) },
                                onClose = host.sites::clearSelection,
                            )
                        }

                        slotKey == "banner" -> {
                            MapBannerSheet {
                                MapBannerAd(
                                    onRemoveAds = viewModel::onRemoveAdsRequested,
                                    removeAdsLoading = state.rewardedLoading,
                                )
                            }
                        }

                        // Slot au repos : rien. Pas meme une Box vide — la mesure retomberait a
                        // une hauteur non nulle et les actions carte resteraient poussees vers
                        // le haut.
                        else -> {
                            Unit
                        }
                    }
                }
            },
        )

        // Annonce pas encore arrivee : la vue de la regie charge ici, a zero de hauteur et a la
        // largeur exacte de la feuille (sinon l'annonce serait demandee pour une autre largeur),
        // sans rien reserver. Des qu'elle arrive la feuille prend le relais dans le slot.
        if (banner.claimed && !banner.loaded) {
            Box(
                modifier =
                    Modifier
                        .align(Alignment.BottomCenter)
                        .widthIn(max = if (expanded) MapChromeDefaults.ExpandedBottomSlotWidth else Dp.Unspecified)
                        .fillMaxWidth()
                        .padding(horizontal = FruxSpacing.md),
            ) {
                MapBannerAd()
            }
        }

        if (showLegend) {
            MapLegendSheet(onDismiss = { showLegend = false })
        }

        if (showNearbyList) {
            NearbyTreesSheet(
                trees = state.nearbySorted,
                speciesHarvests = state.speciesHarvests,
                currentMonth = state.currentMonth,
                now = state.now,
                reference = state.distanceReference,
                aroundUser = state.userLocation != null,
                seasonalAround = state.seasonalAround,
                onSeasonSpeciesClick = { species ->
                    showNearbyList = false
                    viewModel.onSpeciesSelected(species)
                },
                onSeeAllClick = {
                    showNearbyList = false
                    onSeeAllClick(viewModel.seeAllContext())
                },
                onTreeClick = { treeId ->
                    // Refermer AVANT de selectionner : la fiche de l'arbre monte dans le slot bas,
                    // que la feuille recouvrirait sinon.
                    showNearbyList = false
                    viewModel.selectTree(treeId, SelectionOrigin.LIST)
                },
                onDismiss = { showNearbyList = false },
                onRemoveAds = viewModel::onRemoveAdsRequested,
                removeAdsLoading = state.rewardedLoading,
            )
        }

        if (showFilters) {
            val seasonHighlights =
                remember(state.species, state.currentMonth) {
                    state.species.inSeasonHighlights(state.currentMonth)
                }
            MapFiltersSheet(
                filter = state.filter,
                hasSpeciesFilter = state.selectedSpeciesRollupId != null,
                seasonHighlights = seasonHighlights,
                selectedSpeciesRollupId = state.selectedSpeciesRollupId,
                onSpeciesSelected = viewModel::onSpeciesSelected,
                onFilterChange = viewModel::onFilterChange,
                onClearFilters = viewModel::clearFilters,
                onDismiss = { showFilters = false },
            )
        }

        if (showContributionGuide) {
            ContributionGuideSheet(
                onDismiss = { showContributionGuide = false },
                onConfirm = {
                    showContributionGuide = false
                    onAddTree(true)
                },
            )
        }

        state.firstContribution?.let { celebration ->
            FirstContributionSheet(
                celebration = celebration,
                onSeeOnMap = viewModel::consumeFirstContribution,
                onAddAnother = {
                    viewModel.consumeFirstContribution()
                    onAddAnotherTree()
                },
            )
        }

        SnackbarHost(
            hostState = snackbarHostState,
            modifier =
                Modifier
                    .align(Alignment.BottomCenter)
                    .padding(bottom = bottomClearance),
        ) { FruxSnackbar(it) }
    }
}
